import styled, { css, keyframes } from 'styled-components';
import { MODAL_TRANSITION_DURATION } from '@/app/components/search/components/Modal/OpenXModal/utils.ts';
import { MODAL_DESKTOP_BREAKPOINT_PX, MODAL_MOBILE_BREAKPOINT_PX } from '@/app/components/search/utils/useSsrMedia.ts';
import { Dialog } from '@lmn/design-system';
import type { Brand } from '@/domainLogic/index.ts';
import type { MODAL_THEME_VARIANTS } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { variantStyles } from '@/app/components/search/components/Modal/OpenXModal/variants.styled.ts';
import {
  comparisonModal,
  customCheckinCheckoutMobileModal,
  customCheckinCheckoutModal,
  filterModal,
  flexibleAlternativeDatesModal,
  hotelGalleryModal,
  hotelMapModal,
  hotelModalLateral,
  hotelModalLateralEvo,
  hotelReviewsModal,
  inlineDatePickerModal,
  inlineDatePickerModalMobile,
  mobileModalFullHeight,
  packageDetailsModal,
  packageSavingsModal,
  peopleSelectorModal,
  roomDetailsModal,
  roomListModal,
  sortModalMobile,
  travelExtraPackModal,
  voucherModalEvo,
} from '@/app/components/search/components/Modal/OpenXModal/overrides.styled.ts';

const mobileContentShow = keyframes`
  from { opacity: 0; transform: translateY(100%); }
  to   { opacity: 1; transform: translateY(0);    }
`;

const mobileContentHide = keyframes`
  from { opacity: 1; transform: translateY(0);    }
  to   { opacity: 0; transform: translateY(100%); }
`;

// Desktop: dialog is centered with translate(-50%, -50%), so the slide-up
// must compose with that transform to match production (MUI Slide direction="up").
const desktopContentShow = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) translateY(100vh); }
  to   { opacity: 1; transform: translate(-50%, -50%) translateY(0);     }
`;

const desktopContentHide = keyframes`
  from { opacity: 1; transform: translate(-50%, -50%) translateY(0);     }
  to   { opacity: 0; transform: translate(-50%, -50%) translateY(100vh); }
`;

const lateralContentShow = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0);    }
`;

const lateralContentHide = keyframes`
  from { transform: translateX(0);    }
  to   { transform: translateX(100%); }
`;

// Transparent fixed-position blocker rendered for ~400 ms after a modal closes.
// It sits below the Radix overlay (z-index 1300) while the overlay is still animating
// out, then absorbs the ghost/phantom click that iOS Safari fires after the overlay
// has already been removed from the DOM.
export const GhostClickBlocker = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1299;
`;

const MODAL_XXL = '736px';
const MODAL_XL = '600px';
const MODAL_L_EVO = '520px';
const MODAL_L = '480px';
const MODAL_M = '400px';
const MODAL_S = '375px';

const MODAL_MAX_WIDTHS: Record<MODAL_SIZES, string> = {
  [MODAL_SIZES.XXL]: MODAL_XXL,
  [MODAL_SIZES.XL]: MODAL_XL,
  [MODAL_SIZES.L_EVO]: MODAL_L_EVO,
  [MODAL_SIZES.L]: MODAL_L,
  [MODAL_SIZES.M]: MODAL_M,
  [MODAL_SIZES.S]: MODAL_S,
};

const modalSizesStyles = css<{ size?: MODAL_SIZES }>`
  &.modal-full-height {
    height: calc(100% - 64px) !important;
  }

  ${({ size }) =>
    size &&
    css`
      @media (min-width: ${MODAL_DESKTOP_BREAKPOINT_PX}px) {
        max-width: ${MODAL_MAX_WIDTHS[size]} !important;
      }
    `}
`;

const paddingStyles = css`
  &.padding-0 {
    [class*='Dialogstyles__Content'] {
      padding: 0;
    }
  }
`;

export const ModalCustomOverrides = styled(Dialog)<{
  brand: Brand;
  size?: MODAL_SIZES;
  $variant?: MODAL_THEME_VARIANTS;
  $disableAnimation?: boolean;
  $scrollShadows?: boolean;
}>`
  ${modalSizesStyles};
  ${paddingStyles};
  ${sortModalMobile};
  ${customCheckinCheckoutModal};
  ${customCheckinCheckoutMobileModal};
  ${inlineDatePickerModal};
  ${inlineDatePickerModalMobile};
  ${filterModal};
  ${travelExtraPackModal};
  ${comparisonModal};
  ${roomDetailsModal};
  ${roomListModal};
  ${mobileModalFullHeight}
  ${packageDetailsModal};
  ${packageSavingsModal};
  ${hotelGalleryModal};
  ${hotelMapModal};
  ${hotelReviewsModal};
  ${flexibleAlternativeDatesModal};
  ${hotelModalLateral};
  ${hotelModalLateralEvo};
  ${peopleSelectorModal};
  ${voucherModalEvo};
  ${variantStyles};

  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};

  ${({ fullScreen }) =>
    !fullScreen &&
    css`
      @media (min-width: ${MODAL_DESKTOP_BREAKPOINT_PX}px) {
        max-width: ${MODAL_XL};
      }
    `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
    outline-offset: 2px;
  }

  [class*='DialogHeader__HeaderContainer'] {
    svg {
      width: 16px;
      height: 16px;
    }
  }

  [class*='DialogFooter__FooterContainer'] {
    padding: 16px;
  }

  [class*='Dialogstyles__Body'] {
    overscroll-behavior: none;
  }

  &.openx-ui-all-filters-modal {
    [class*='Dialogstyles__Footer'] {
      position: sticky;
      bottom: 0;
      z-index: 1;
    }
  }

  ${({ $scrollShadows }) =>
    $scrollShadows &&
    css`
      [class*='Dialogstyles__Header'],
      [class*='DialogHeader__HeaderContainer'] {
        box-shadow: none !important;
        border-bottom: 0;
      }
      [class*='Dialogstyles__Footer'] {
        position: sticky;
        bottom: 0;
        z-index: 1;
      }
      [class*='DialogFooter__FooterContainerStyled'] {
        box-shadow: none !important;
        border-top: none !important;
        padding: 0 !important;
      }
    `}

  ${({ $disableAnimation }) =>
    !$disableAnimation &&
    css`
      @media (max-width: ${MODAL_MOBILE_BREAKPOINT_PX}px) {
        &[data-state='open'] {
          animation: ${mobileContentShow} ${MODAL_TRANSITION_DURATION}ms ease-out;
        }
        &[data-state='closed'] {
          animation: ${mobileContentHide} ${MODAL_TRANSITION_DURATION}ms ease-in;
        }
      }

      @media (min-width: ${MODAL_DESKTOP_BREAKPOINT_PX}px) {
        &[data-state='open'] {
          animation: ${desktopContentShow} ${MODAL_TRANSITION_DURATION}ms ease-out;
        }
        &[data-state='closed'] {
          animation: ${desktopContentHide} ${MODAL_TRANSITION_DURATION}ms ease-in;
        }
      }
    `};

  @media (min-width: 768px) {
    &.hotel-modal-lateral,
    &.hotel-modal-lateral-evo,
    &.hotel-gallery-modal {
      &[data-state='open'] {
        animation: ${lateralContentShow} ${MODAL_TRANSITION_DURATION}ms ease-out;
      }
      &[data-state='closed'] {
        animation: ${lateralContentHide} ${MODAL_TRANSITION_DURATION}ms ease-in;
      }
    }
  }
`;
